import { useEffect, useMemo, useState } from 'react'
import LinkIcon from '@mui/icons-material/Link'
import { Autocomplete, Chip, MenuItem, Stack, TextField } from '@mui/material'
import useAxios from '../../hooks/useAxios'
import { IShopeeAccount } from '../../types/shopee'
import { platformLinkKey } from '../../utils/platform-link.util'

export interface IShopeeSkuOption {
  itemId: string
  itemName: string
  sku: string // model_sku ถ้ามี variation ไม่งั้น item_sku — ตรงกับที่ webhook ใช้ตัดสต๊อก
  modelId: string // '' = สินค้าไม่มี variation
  shopId: string
  shopName: string
}

interface IShopeeProductModel {
  modelId: string
  modelSku: string
  modelName: string
}

interface IShopeeProduct {
  id: string
  name: string
  itemSku: string
  models?: IShopeeProductModel[]
}

interface Props {
  value?: string // model_id ที่ผูกไว้ (ตกไป item_id ของ referrer เดิมได้)
  onSelect: (value: IShopeeSkuOption | null) => void
  label?: string
  error?: boolean | any
  helperText?: string | any
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  // key ของ SKU ที่ถูกผูกไปแล้ว → ข้อความบอกว่าใครใช้อยู่ (ดู platformLinkKey)
  // ผูก SKU เดียวกับ 2 สินค้า = ตัดสต๊อกไม่ได้ทั้งคู่ (ambiguous) จึงปิดไม่ให้เลือก
  usedKeys?: Map<string, string>
}

const ShopeeProductSelectSearch = (props: Props) => {
  const {
    value,
    onSelect,
    label = 'เลือกสินค้า Shopee',
    error,
    helperText,
    onBlur,
    usedKeys,
  } = props
  const [shopId, setShopId] = useState<string>('')

  const { data: accounts, isLoading: accountsLoading } = useAxios<
    IShopeeAccount[]
  >({
    method: 'GET',
    url: '/shopee/merchant/accounts',
  })

  // auto-pick บัญชีแรกเมื่อ accounts โหลดเสร็จ
  useEffect(() => {
    if (!shopId && accounts && accounts.length > 0) {
      setShopId(accounts[0].shopId)
    }
  }, [accounts, shopId])

  // ดึงสินค้าเฉพาะเมื่อมี shopId แล้ว (request=null → useSWR ไม่ fetch)
  const { data: products, isLoading: productsLoading } = useAxios<
    IShopeeProduct[]
  >(
    shopId
      ? {
          method: 'GET',
          url: '/shopee/products',
          params: { shopId, page: 1, pageSize: 50 },
        }
      : null,
  )

  // สินค้ามี variation → 1 ตัวเลือกต่อ model, ไม่มี variation → ใช้ item_sku ตัวเดียว
  // พ่วง shopName มาด้วยเพื่อให้ผู้เรียกเก็บชื่อร้านลง platformLinks ได้ (id ล้วนอ่านไม่ออก)
  const options = useMemo<IShopeeSkuOption[]>(() => {
    if (!products) {
      return []
    }
    const shopName =
      accounts?.find((account) => account.shopId === shopId)?.sellerName ?? ''
    return products.flatMap((product) => {
      const models = product.models || []
      if (!models.length) {
        return [
          {
            itemId: product.id,
            itemName: product.name,
            sku: product.itemSku,
            modelId: '',
            shopId,
            shopName,
          },
        ]
      }
      return models.map((model) => ({
        itemId: product.id,
        itemName: product.name,
        sku: model.modelSku || product.itemSku,
        modelId: model.modelId,
        shopId,
        shopName,
      }))
    })
  }, [products, shopId, accounts])

  // value เป็น model_id ก่อน (ตัวผูกแบบใหม่) แล้วค่อยตกไป item_id (referrer เดิม)
  const selectedOption =
    options.find((option) => option.modelId && option.modelId === value) ||
    options.find((option) => option.itemId === value) ||
    null

  const loading = accountsLoading || productsLoading

  // ตัวเลือกที่ถูกผูกไปแล้ว — คืนข้อความบอกเจ้าของ ถ้ายังว่างคืน undefined
  // สินค้าไม่มี variation จะไม่มี model_id → ตกไปใช้ sku เป็น key ตามที่ผูกไว้จริง
  const usedBy = (option: IShopeeSkuOption): string | undefined => {
    const key = platformLinkKey({
      skuId: option.modelId || undefined,
      sku: option.sku,
    })
    return key ? usedKeys?.get(key) : undefined
  }

  return (
    <Stack spacing={2}>
      {/* บัญชี Shopee — แสดงเฉพาะตอนมีหลายบัญชี (บัญชีเดียว auto-pick) */}
      {accounts && accounts.length > 1 && (
        <TextField
          select
          fullWidth
          label="บัญชี Shopee"
          value={shopId}
          onChange={(e) => {
            setShopId(e.target.value)
            onSelect(null)
          }}
        >
          {accounts.map((account) => (
            <MenuItem key={account.shopId} value={account.shopId}>
              {account.sellerName || account.shopId}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Autocomplete<IShopeeSkuOption>
        fullWidth
        options={options}
        loading={loading}
        value={selectedOption}
        disabled={!shopId}
        isOptionEqualToValue={(option, val) =>
          option.itemId === val.itemId && option.modelId === val.modelId
        }
        getOptionLabel={(option) => `${option.itemName} — ${option.sku || '-'}`}
        getOptionDisabled={(option) => !!usedBy(option)}
        renderOption={(optionProps, option) => {
          const used = usedBy(option)
          return (
            <li {...optionProps} key={`${option.itemId}-${option.modelId}`}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <span>
                  {option.itemName} — {option.sku || '-'}
                </span>
                {used && <Chip size="small" label={used} icon={<LinkIcon />} />}
              </Stack>
            </li>
          )
        }}
        noOptionsText={
          shopId ? 'ไม่พบสินค้าในบัญชีนี้' : 'กรุณาเลือกบัญชี Shopee'
        }
        onChange={(_, val) => onSelect(val)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            onBlur={onBlur}
          />
        )}
      />
    </Stack>
  )
}

export default ShopeeProductSelectSearch
